import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  Tooltip,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
  InputAdornment,
  Typography,
  useTheme,
  useMediaQuery,
  TableContainer,
  Chip,
  Box,
  AppBar,
  Toolbar
} from '@material-ui/core';
import {
  MedicalSupplyOderItem,
  MedicalSupplyOrderItemViewModel,
  MedicalSupplyBottleViewModel,
  CreateMedicalSupplyOrderCommand
} from '../../models/injection';
import { useStyles } from './styles';
import { Title, MedicalSupplyBottleSelectModal } from '../../elements';
import { MedicalSuppliesService } from '../../hooks';
import { LocationSearchSelectModal } from '../../components';
import { LocationViewModel, LocationPosition, LocationStable } from '../../models/locations';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';

interface OrderMedicalSupplyModalProps {
  opened: boolean;
  onModalAction: (result: boolean | null) => void;
}

const OrderMedicalSupplyModal: React.FC<any> = ({ opened, onModalAction }: OrderMedicalSupplyModalProps) => {
  const medicalSuppliesService = new MedicalSuppliesService();
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [locationSearchOpened, setLocationSearchOpened] = React.useState(false);

  const [medicalSupplyOrderItems, setMedicalSupplyOrderItems] = React.useState([] as MedicalSupplyOderItem[]);
  const [comments, setComments] = React.useState('');

  const [totalPositions, setTotalPositions] = React.useState(0);
  const [totalAmount, setTotalAmount] = React.useState(0);

  React.useEffect(() => {
    if (opened) {
      // initially add new empty order item
      handleAddOrderItem();
    }
  }, [opened]);

  React.useEffect(() => {
    setTotalPositions(medicalSupplyOrderItems.length);
    setTotalAmount(medicalSupplyOrderItems.map((x) => x.amount).reduce((a, b) => a + b, 0));
  }, [medicalSupplyOrderItems]);

  const closeDialog = (result: boolean) => {
    if (onModalAction) {
      onModalAction(result);
    }

    clearFields();
  };

  const handleSubmitOrder = () => {
    if (!validate()) {
      return;
    }

    const order = {
      items: medicalSupplyOrderItems,
      details: {
        comments
        //animalAmount, totalWeight, averageWeight
      }
    } as CreateMedicalSupplyOrderCommand;

    setIsBusy(true);

    medicalSuppliesService
      .createMedicalSupplyOrder(order)
      .then(() => {
        notifications.success(t('f-supp-order-create-ok'));
        closeDialog(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const validate = (): boolean => {
    if (!medicalSupplyOrderItems.length) {
      notifications.warning(t('f-val-supp-order-supp-num'));
      return false;
    }

    if (medicalSupplyOrderItems.filter((x) => !x.bottle?.id).length > 0) {
      notifications.warning(t('f-val-supp-order-supp'));
      return false;
    }

    if (medicalSupplyOrderItems.filter((x) => x.amount <= 0).length > 0) {
      notifications.warning(t('f-val-supp-order-supp-amnt'));
      return false;
    }

    return true;
  };

  const clearFields = () => {
    setMedicalSupplyOrderItems([]);
    setComments('');
  };

  const renderSuppliesSectionHeader = () => {
    return (
      <Grid item container direction="row" className={classes.mt2} alignItems="center">
        <Grid item container md={9} sm={8} xs={12} spacing={1}>
          <Icon color="primary" fontSize="large">
            {'local_hospital'}
          </Icon>
          <Box ml={1} mt={0.5}>
            <Title>{t('f-supp-order-create-title')}</Title>
          </Box>
          <Grid item>
            <Chip label={totalPositions} variant="outlined" />
          </Grid>
        </Grid>

        <Grid item md={3} sm={4} xs={12}>
          <Button
            id="addItemBtn"
            onClick={handleAddOrderItem}
            size="small"
            fullWidth
            variant="outlined"
            startIcon={<Icon>{'add_circle_outline'}</Icon>}
            color="primary"
            className={classes.addOrderBtn}
          >
            {t('f-supp-order-create-add')}
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderDetailsSectionHeader = () => {
    return (
      <Grid item container direction="row" md={12} className={classes.mt2}>
        <Grid item container md={8}>
          <Grid item>
            <Icon color="primary" fontSize="large">
              {'comment'}
            </Icon>
          </Grid>

          <Grid item>
            <Box ml={1}>
              <Title>{t('f-supp-order-details')}</Title>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderDetailsSectionContent = () => {
    return (
      <Grid item container direction="column">
        <Grid item container direction="row" spacing={1}>
          <Grid item md={6} xs={12}>
            <TextField
              label={t('f-supp-order-comments')}
              variant="outlined"
              type="text"
              multiline
              value={comments}
              size="small"
              fullWidth
              onChange={(e) => {
                setComments(e.target.value);
              }}
            />
          </Grid>

          <Grid item md={3} xs={6}>
            <TextField
              label={t('f-supp-order-total-pos')}
              variant="outlined"
              type="number"
              disabled
              InputProps={{
                readOnly: true
              }}
              value={totalPositions}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item md={3} xs={6}>
            <TextField
              label={t('f-supp-order-total-amount')}
              variant="outlined"
              type="number"
              disabled
              InputProps={{
                readOnly: true
              }}
              value={totalAmount}
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderOrderItemsTable = () => {
    return (
      <Grid item xs={12}>
        <TableContainer component={Paper} variant="outlined" style={{ height: '55vh' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('f-supp-bottle')}</TableCell>
                <TableCell>{t('f-com-amount')}</TableCell>
                <TableCell>{t('f-supp-order-locations')}</TableCell>
                <TableCell>{t('f-supp-order-symp')}</TableCell>
                <TableCell>{t('f-farm-anim-count')}</TableCell>
                <TableCell>{t('f-animal-avg-weight')}</TableCell>
                <TableCell>{t('f-com-action')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {medicalSupplyOrderItems.map((supply, index) => (
                <TableRow key={index}>
                  <TableCell>{renderOrderItemBottleCell(supply, index)}</TableCell>
                  <TableCell>{renderOrderItemAmountCell(supply, index)}</TableCell>
                  <TableCell>{renderOrderItemLocationsCell(supply, index)}</TableCell>
                  <TableCell>{renderSupplyRowReasonInput(supply, index)}</TableCell>
                  <TableCell>{renderSupplyAnimalCountInput(supply, index)}</TableCell>
                  <TableCell>{renderSupplyAvgWeightInput(supply, index)}</TableCell>
                  <TableCell>{renderSupplyRowActionCell(supply, index)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    );
  };

  const renderOrderItemBottleCell = (row: MedicalSupplyOderItem, index: number) => {
    return (
      <MedicalSupplyBottleSelectModal
        variant="outlined"
        disableAny
        bottle={row.bottle}
        key={`supply-select-${index}`}
        onChange={(value) => handleOrderItemBottleChange(value, index)}
      />
    );
  };

  const renderOrderItemAmountCell = (row: MedicalSupplyOderItem, index: number) => {
    return (
      <TextField
        id={`amount-input-${index}`}
        type="number"
        variant="outlined"
        InputProps={{ startAdornment: <InputAdornment position="start">{'x'}</InputAdornment> }}
        className={classes.amountInput}
        value={row.amount}
        onChange={(e) => {
          const newValue = Number(e.target.value);
          if (newValue >= 0) {
            handleOrderItemAmountChange(newValue, index);
          }
        }}
      />
    );
  };

  const renderOrderItemLocationsCell = (row: MedicalSupplyOderItem, index: number) => {
    return (
      <Grid container direction="row">
        <Grid item>{renderSupplyRowLocations(row, index)}</Grid>

        <Grid item>
          <Tooltip arrow title={t('f-supp-order-locations-arrow')}>
            <IconButton size="small" onClick={() => handleSpecifyLocation(index)} color="primary">
              <Icon>{'add_location'}</Icon>
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    );
  };

  const renderSupplyRowReasonInput = (row: MedicalSupplyOderItem, index: number) => {
    return (
      <TextField
        id={`reson-input-${index}`}
        type="text"
        variant="outlined"
        multiline
        className={classes.maxWidthField}
        value={row.symptoms || ''}
        onChange={(e) => {
          handleOrderItemReasonChange(e.target.value, index);
        }}
      />
    );
  };

  const renderSupplyAnimalCountInput = (row: MedicalSupplyOderItem, index: number) => {
    return (
      <TextField
        id="animal-count-field"
        label={''}
        variant="outlined"
        className={classes.amountInput}
        type="number"
        value={row.details?.animalAmount || 0}
        onChange={(e) => {
          const newValue = Number(e.target.value);
          if (newValue >= 0) {
            handleOrderItemAnimalAmountChange(newValue, index);
          }
        }}
      />
    );
  };

  const renderSupplyAvgWeightInput = (row: MedicalSupplyOderItem, index: number) => {
    return (
      <TextField
        id="avg-weight-field"
        label={''}
        variant="outlined"
        className={classes.amountInput}
        type="number"
        value={row.details?.averageWeight || 0}
        onChange={(e) => {
          const newValue = Number(e.target.value);
          if (newValue >= 0) {
            handleOrderItemAvgWeightChange(newValue, index);
          }
        }}
      />
    );
  };

  const renderSupplyRowActionCell = (_row: MedicalSupplyOderItem, index: number) => {
    return (
      <Tooltip arrow title={t('f-supp-order-supp-rem')}>
        <IconButton size="small" onClick={() => handleRemoveOrderItem(index)} color="secondary">
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const handleSpecifyLocation = (index: number) => {
    setLocationSearchOpened(true);
    setLocationOrderItemIndex(index);
  };
  const [locationOrderItemIndex, setLocationOrderItemIndex] = React.useState(0);

  const handleLocationSelection = (locations: LocationViewModel[] | null) => {
    setLocationSearchOpened(false);

    if (locations === null) return;

    medicalSupplyOrderItems[locationOrderItemIndex].locations = locations;
    setMedicalSupplyOrderItems([...medicalSupplyOrderItems]);
  };

  const handleRemoveOrderItem = (index: number) => {
    medicalSupplyOrderItems.splice(index, 1);
    setMedicalSupplyOrderItems([...medicalSupplyOrderItems]);
  };

  const handleOrderItemBottleChange = (bottle: MedicalSupplyBottleViewModel | null, index: number) => {
    medicalSupplyOrderItems[index].bottle = bottle;
    medicalSupplyOrderItems[index].bottleId = bottle?.id || '';
    setMedicalSupplyOrderItems([...medicalSupplyOrderItems]);
  };

  const handleOrderItemAmountChange = (amount: number, index: number) => {
    medicalSupplyOrderItems[index].amount = amount;
    setMedicalSupplyOrderItems([...medicalSupplyOrderItems]);
  };

  const handleOrderItemAnimalAmountChange = (amount: number, index: number) => {
    medicalSupplyOrderItems[index].details.animalAmount = amount;
    setMedicalSupplyOrderItems([...medicalSupplyOrderItems]);
  };

  const handleOrderItemAvgWeightChange = (avgWeight: number, index: number) => {
    medicalSupplyOrderItems[index].details.averageWeight = avgWeight;
    setMedicalSupplyOrderItems([...medicalSupplyOrderItems]);
  };

  const handleOrderItemReasonChange = (reason: string, index: number) => {
    medicalSupplyOrderItems[index].symptoms = reason;
    setMedicalSupplyOrderItems([...medicalSupplyOrderItems]);
  };

  const handleAddOrderItem = () => {
    const orderItem = { bottle: null, amount: 1, details: {} } as MedicalSupplyOderItem;
    setMedicalSupplyOrderItems([...medicalSupplyOrderItems, orderItem]);
  };

  return (
    <Dialog
      PaperProps={{ style: { minHeight: 'calc(100vh - 64px)' } }}
      id="orderSupplyDialog"
      open={opened}
      onClose={() => closeDialog(false)}
      className={classes.dialog}
      maxWidth="lg"
      fullScreen={fullScreen}
      fullWidth
      aria-labelledby="form-dialog-title"
      // The bottle picker opened from this dialog is MUI v9, whose focus trap knows nothing of this v4 one:
      // with both enforcing focus they pull it back and forth until the stack overflows and nothing takes input.
      disableEnforceFocus
    >
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <Box width="100%" justifyContent="space-between" display="flex">
              <Box display="flex" alignItems="center">
                <IconButton edge="start" color="inherit" onClick={() => closeDialog(false)} aria-label="close">
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6">{t('f-supp-order-create-new')}</Typography>
              </Box>

              <Tooltip title={t('f-supp-order-place')} arrow>
                <IconButton onClick={() => handleSubmitOrder()} id="saveBtn" disabled={isBusy} color="inherit">
                  <Icon>done</Icon>
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle id="orderSupplyDialogTitle">{t('f-supp-order-create-new')}</DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container direction="column" spacing={1}>
          {renderSuppliesSectionHeader()}

          {renderOrderItemsTable()}

          {renderDetailsSectionHeader()}

          {renderDetailsSectionContent()}
        </Grid>
      </DialogContent>

      <DialogActions>
        {isBusy && <CircularProgress />}

        <Button
          onClick={() => closeDialog(false)}
          startIcon={<Icon>{'close'}</Icon>}
          disabled={isBusy}
          variant="outlined"
          color="primary"
        >
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={() => handleSubmitOrder()}
          id="saveBtn"
          startIcon={<Icon>{'done_outline'}</Icon>}
          disabled={isBusy}
          variant="contained"
          color="primary"
        >
          {t('f-supp-order-place')}
        </Button>
      </DialogActions>

      <LocationSearchSelectModal opened={locationSearchOpened} onModalAction={handleLocationSelection} />
    </Dialog>
  );
};

export default OrderMedicalSupplyModal;

export function renderSupplyRowLocations(row: MedicalSupplyOderItem | MedicalSupplyOrderItemViewModel, _index: number) {
  if (!row.locations) return <Icon>{'location_off'}</Icon>;

  return (
    <div style={{ minWidth: 125 }}>
      {row.locations.map((loc) => (
        <Grid item container key={loc.id} direction="column">
          <Grid item>
            <Typography variant="body2">{`${loc.room} ${LocationPosition[loc.positioning]}`}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="caption">{`${loc.pen} ${LocationStable[loc.stable]}`}</Typography>
          </Grid>
        </Grid>
      ))}
    </div>
  );
}
